"use client";

import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import { __, getPermalink } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import LogoutCustomer from "@/components/templates/Account/components/logout-customer";
import { actions } from "@/locales/segment";
import AccountViewFilter from "@/components/templates/Account/components/account-view-filter";
import WishlistSidebar from "@/components/templates/Account/components/wishlist-sidebar";
import { useCheckoutStore } from "@/stores/checkout-store";

type AccountSidebarProps = {
  slugs: string[];
  locale: Locales;
};

export const getSidebarLinks = (locale: Locales, accountType: "individual" | "company") => {
  const baseLinks = [
    {
      label: __("My information", locale),
      link: getPermalink(locale, "account"),
    },
    {
      label: __("Addresses", locale),
      link: getPermalink(locale, "account", "address"),
    },
    {
      label: __("My favorites", locale),
      link: getPermalink(locale, "account", "wishlist"),
    },
    {
      label: __("Orders", locale),
      link: getPermalink(locale, "account", "orders"),
    },
  ];

  if (accountType === "company") {
    baseLinks.push({
      label: __("Company", locale),
      link: getPermalink(locale, "account", "business"),
    });
  }

  return baseLinks;
};

export const isEditSlug = (locale: Locales, slug: string) => {
  const localeActions = actions[locale as keyof typeof actions];
  if (!localeActions) return false;
  return localeActions.edit === slug;
};

const isWishlistSlug = (locale: Locales, slug: string) => {
  const localeActions = actions[locale as keyof typeof actions];
  if (!localeActions) return false;
  return localeActions.wishlist === slug;
};

export const isOrdersSlug = (locale: Locales, slug: string) => {
  const localeActions = actions[locale as keyof typeof actions];
  if (!localeActions) return false;
  return localeActions.orders === slug;
};

const AccountSidebar = ({ slugs, locale }: AccountSidebarProps) => {
  const accountType = useCheckoutStore((state) => state.accountType);
  const links = getSidebarLinks(locale, accountType || "individual");
  const currentSlug = slugs[0];

  if (isWishlistSlug(locale, currentSlug)) {
    return <WishlistSidebar locale={locale} />;
  }

  return (
    <aside className="flex-1">
      <nav className="hidden lg:block" aria-label="Account Sidebar Navigation">
        <ul className="w-full min-w-[17.5rem] space-y-3 lg:max-w-[21.375rem]">
          {links.map((link, index) => {
            const linkSlug = link.link.split("/").pop();
            let isActive = currentSlug === linkSlug;

            // Special case: if current slug is "edit" and this is the first link (My information), mark as active
            if (index === 0 && isEditSlug(locale, currentSlug)) {
              isActive = true;
            }

            return (
              <li key={index}>
                <Link
                  href={link.link}
                  className={cn(
                    "group hover:text-dark-primary flex items-center justify-between rounded-sm px-2.5 py-3 font-semibold transition-all duration-200 ease-in-out hover:bg-[#fafaf9]",
                    "text-dark-primary focus-visible:bg-pink-primary focus-visible:text-white-primary active:bg-pink-primary active:text-white",
                    isActive && "bg-pink-primary text-white"
                  )}
                  aria-current={isActive ? "page" : undefined}
                >
                  {link.label}
                  <ChevronRight
                    className={cn("hidden group-focus-visible:block group-active:block", isActive && "block")}
                    size={24}
                    aria-hidden="true"
                  />
                </Link>
              </li>
            );
          })}
          <hr className="border-black/19" />
          <li>
            <LogoutCustomer locale={locale} />
          </li>
        </ul>
      </nav>
      <AccountViewFilter locale={locale} links={links} currentSlug={currentSlug} slugs={slugs} />
    </aside>
  );
};

export default AccountSidebar;
